import { use } from 'echarts/core'
import { BarChart, LineChart, PieChart } from 'echarts/charts'
import {
  DatasetComponent,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { LabelLayout, UniversalTransition } from 'echarts/features'

/**
 * Registrasi modular ECharts (tree-shakeable) — hanya modul yang dipakai
 * yang masuk ke bundle. Dipanggil sekali di main.ts.
 */
export function setupECharts() {
  use([
    CanvasRenderer,
    LineChart,
    BarChart,
    PieChart,
    GridComponent,
    TooltipComponent,
    LegendComponent,
    TitleComponent,
    DatasetComponent,
    GraphicComponent,
    TransformComponent,
    LabelLayout,
    UniversalTransition,
  ])
}

/**
 * Palet warna pastel / soft yang harmonis, bervariasi, dan ramah mata.
 * Sangat cocok untuk grafik distribusi seperti Pie / Donut kategori produk.
 */
export const PASTEL_CHART_COLORS = [
  '#8FCFA5', // Soft Sage Green (Warna Utama Brand)
  '#93C5FD', // Soft Sky Blue
  '#FCA5A5', // Soft Coral Rose
  '#FDBA74', // Soft Peach / Warm Apricot
  '#C4B5FD', // Soft Lavender Purple
  '#FDE047', // Soft Butter Yellow
  '#6EE7B7', // Soft Seafoam / Mint Green
  '#F472B6', // Soft Dusty Rose Pink
  '#67E8F9', // Soft Aqua / Cyan
  '#FBBF24', // Soft Amber Gold
  '#A78BFA', // Soft Periwinkle
  '#A3E635', // Soft Matcha / Lime
  '#FB7185', // Soft Berry Strawberry
  '#38BDF8', // Soft Cerulean
  '#E879F9', // Soft Orchid Mauve
  '#2DD4BF', // Soft Teal Breeze
  '#FED7AA', // Soft Cantaloupe
  '#DDD6FE', // Soft Lilac Mist
  '#BAE6FD', // Soft Powder Blue
  '#FECDD3', // Soft Blush Peach
]

/**
 * Konversi warna CSS (seperti oklch(...), var(...)) menjadi format #rrggbb HEX
 * yang dijamin dapat di-parse oleh ZRender (mesin rendering ECharts) dan CanvasGradient.
 */
const colorCache = new Map<string, string>()

function normalizeColor(cssColor: string, fallback: string = '#8FCFA5'): string {
  if (!cssColor) return fallback
  if (colorCache.has(cssColor)) return colorCache.get(cssColor)!

  let normalized = fallback
  if (/^#[0-9a-fA-F]{6}$/.test(cssColor)) {
    normalized = cssColor
  } else if (cssColor.startsWith('#') && cssColor.length === 4) {
    normalized =
      '#' + cssColor[1] + cssColor[1] + cssColor[2] + cssColor[2] + cssColor[3] + cssColor[3]
  } else if (typeof document !== 'undefined') {
    try {
      const ctx = document.createElement('canvas').getContext('2d')
      if (ctx) {
        ctx.fillStyle = cssColor
        const computed = ctx.fillStyle
        if (computed.startsWith('#')) {
          normalized = computed
        } else if (computed.startsWith('rgb')) {
          const match = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
          if (match) {
            normalized = `#${((1 << 24) + (Number(match[1]) << 16) + (Number(match[2]) << 8) + Number(match[3])).toString(16).slice(1)}`
          }
        }
      }
    } catch {
      // ignore
    }
  }

  colorCache.set(cssColor, normalized)
  return normalized
}

/**
 * Helper untuk membuat string rgba(...) dari warna hex #rrggbb dengan nilai opacity.
 */
export function colorWithAlpha(hexColor: string, opacity: number): string {
  if (hexColor.startsWith('#') && hexColor.length === 7) {
    const r = parseInt(hexColor.slice(1, 3), 16)
    const g = parseInt(hexColor.slice(3, 5), 16)
    const b = parseInt(hexColor.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${opacity})`
  }
  return hexColor
}

/**
 * Membaca token warna dari CSS variable aktif (mengikuti tema light/dark).
 * Hasil dikonversi ke format HEX #rrggbb yang aman untuk ZRender/ECharts.
 */
export function readChartPalette() {
  const styles = getComputedStyle(document.documentElement)
  const v = (name: string) => styles.getPropertyValue(name).trim()

  const rawChartColors = [
    v('--color-chart-1'),
    v('--color-chart-2'),
    v('--color-chart-3'),
    v('--color-chart-4'),
    v('--color-chart-5'),
  ].filter(Boolean)

  const defaultChartColors = [
    '#8FCFA5',
    '#3b82f6',
    '#f97316',
    '#22c55e',
    '#ef4444',
    '#a855f7',
    '#ec4899',
  ]

  const chartColors =
    rawChartColors.length > 0
      ? rawChartColors.map((c) => normalizeColor(c, '#8FCFA5'))
      : defaultChartColors

  return {
    primary: normalizeColor(v('--color-primary'), '#8FCFA5'),
    card: normalizeColor(v('--color-card'), '#ffffff'),
    chart: chartColors,
    foreground: normalizeColor(v('--color-foreground'), '#1f2937'),
    muted: normalizeColor(v('--color-muted-foreground'), '#6b7280'),
    border: normalizeColor(v('--color-border'), '#e5e7eb'),
    pastels: PASTEL_CHART_COLORS,
  }
}
